import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const BillingSwitch = () => {
  const { user, reload } = useAuth();
  const nav = useNavigate();
  const [pick, setPick] = useState(null);
  const [cid, setCid] = useState("");
  const isAdmin = user.role === "superadmin";
  if (user.acting_studio || (user.role === "studio" && user.studio_own_company)) {
    const toCompany = !user.acting_studio;
    const swap = async () => {
      try {
        await api.post("/auth/billing-mode", toCompany ? { on: true, company_id: user.studio_own_company } : { on: false });
        window.location.href = toCompany ? "/app" : "/studio";
      } catch (e) { toast.error(errMsg(e)); }
    };
    return (
      <Button size="sm" variant="outline" data-testid={toCompany ? "switch-to-own-company-button" : "switch-to-studio-button"} onClick={swap}>
        <ArrowLeftRight className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{toCompany ? "Passa alla mia azienda" : "Torna allo studio"}</span>
      </Button>
    );
  }
  if (!isAdmin && !user.acting_superadmin) return null;

  const go = async (on, company_id) => {
    try {
      await api.post("/auth/billing-mode", { on, company_id });
      await reload();
      nav(on ? "/app" : "/admin");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const start = async () => {
    if (user.own_company_id) {
      try {
        await api.post("/auth/billing-mode", { on: true });
        await reload();
        return nav("/app");
      } catch {
        await reload();
      }
    }
    const { data } = await api.get("/admin/companies");
    setPick(data.filter((c) => !c.demo));
  };

  return (
    <>
      <Button size="sm" variant="outline" data-testid={isAdmin ? "switch-to-billing-button" : "switch-to-admin-button"} onClick={() => (isAdmin ? start() : go(false))}>
        <ArrowLeftRight className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{isAdmin ? "Passa a fatturazione" : "Torna all'amministrazione"}</span>
      </Button>
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Con quale azienda fatturi?</DialogTitle></DialogHeader>
          {pick?.length === 0 ? (
            <p className="text-sm text-slate-600">Crea prima la tua azienda in <b>Aziende</b> (con i dati fiscali dello studio) e assegnale un pacchetto XML, poi torna qui.</p>
          ) : (
            <>
              <Select value={cid || undefined} onValueChange={setCid}>
                <SelectTrigger data-testid="billing-company-select"><SelectValue placeholder="Seleziona la tua azienda…" /></SelectTrigger>
                <SelectContent>{(pick || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500">La scelta viene ricordata. Le fatture consumano gli XML del pacchetto assegnato a questa azienda.</p>
            </>
          )}
          <DialogFooter>
            {pick?.length === 0
              ? <Button data-testid="billing-go-companies" onClick={() => { setPick(null); nav("/admin/aziende"); }}>Vai ad Aziende</Button>
              : <Button data-testid="billing-company-confirm" disabled={!cid} onClick={() => { setPick(null); go(true, cid); }}>Entra in fatturazione</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
